import { useEffect, useMemo, useState} from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useField } from '@unform/core';
import { CfopService } from '../services/api/cadastros/CfopService';

type TAutoCompleteOption = {
  cfop: number;
  label: string;
}
interface IAutoCompleteCfopProps {
  isExternalLoading?: boolean;
  cfops?: string;
}
export const AutoCompleteCfop: React.FC<IAutoCompleteCfopProps> = ({ isExternalLoading = false, cfops = '' }) => {
  const { fieldName, registerField, defaultValue, error, clearError } = useField('cfop');
  const [selectedId, setSelectedId] = useState<number | undefined>(defaultValue);
  const [opcoes, setOpcoes] = useState<TAutoCompleteOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [busca, setBusca] = useState('');

 
  useEffect(() => {
    registerField({
      name: fieldName,
      getValue: () => selectedId,
      setValue: (_, newSelectedId) => setSelectedId(newSelectedId),
    });
  }, [registerField, fieldName, selectedId]);

  // Ao abrir um item já salvo, `selectedId` muda duas vezes em sequência:
  // primeiro undefined (render inicial, antes do setData do item aplicar o
  // Cfop salvo), depois o Cfop de verdade — cada mudança dispara esse efeito
  // de novo, e as duas buscas ficam correndo em paralelo sem garantia de
  // ordem de resposta. A busca "geral" (sem id, lista grande) tende a demorar
  // mais que a busca pelo Cfop específico — se ela responder DEPOIS, sobrepõe
  // `opcoes` com a lista genérica, que pode não conter esse Cfop, e o campo
  // que já tinha aparecido corretamente fica em branco de novo. `cancelado`
  // garante que só a resposta da busca MAIS RECENTE (a última disparada)
  // atualiza o estado — mesmo padrão usado em Dialogsendnfe.tsx/
  // DetalheDaNfe.tsx pra evitar corrida equivalente.
  useEffect(() => {
    let cancelado = false;
    setIsLoading(true);
    CfopService.getAlllist(busca, selectedId?.toString())
      .then((result) => {
        if (cancelado) return;
        setIsLoading(false);

        if (result instanceof Error) {
          // alert(result.message);
        } else {
          setOpcoes(result.data.map(cfop => ({ cfop: cfop.cfop, label: cfop.cfop + ' - ' + cfop.natureza })));
        }
      });
    return () => { cancelado = true; };
  }, [busca, selectedId]);

  useEffect(() => {
    if(cfops !== ''){
      let cancelado = false;
      setIsLoading(true);
      CfopService.getAlllist(cfops)
        .then((result) => {
          if (cancelado) return;
          setIsLoading(false);

          if (result instanceof Error) {
          // alert(result.message);
          } else {
            setOpcoes(result.data.map(cfop => ({ cfop: cfop.cfop, label: cfop.cfop + ' - ' + cfop.natureza })));
          }
        });
      setSelectedId(Number(cfops));
      return () => { cancelado = true; };
    }
  }, [cfops]);

  const autoCompleteSelectedOption = useMemo(() => {
    if (!selectedId) return null;

    const selectedOption = opcoes.find(opcao => opcao.cfop === selectedId);
    if (!selectedOption) return null;

    return selectedOption;
  }, [selectedId, opcoes]);


  return (
    <Autocomplete
      openText='Abrir'
      closeText='Fechar'
      noOptionsText='Sem opções'
      loadingText='Carregando...'

      disablePortal

      options={opcoes}
      loading={isLoading}
      disabled={isExternalLoading}
      value={autoCompleteSelectedOption}
      // Só busca sozinho por `reason==='input'` (o usuário digitando) — sem isso, o reset
      // automático do MUI ao mudar `value` também vira busca e pode entrar em loop
      // (seleciona → busca pelo rótulo → não acha → desmarca → reseta → repete). Ver
      // AutoCompleteMunicipio.tsx pro caso completo que expôs isso.
      onInputChange={(_, newValue, reason) => {
        if (reason === 'input') setBusca(newValue);
      }}
      onChange={(_, newValue) => { setSelectedId(newValue?.cfop); setBusca(''); clearError(); }}
      popupIcon={(isLoading) ? <CircularProgress size={28} /> : undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          size='small'
          label="CFOP"
          error={!!error}
          helperText={error}
        />
      )}
    />
  );
};